// ═══════════════════════════════════════════════════════════════
// ANALYTICS TYPES — Single Source of Truth
// ═══════════════════════════════════════════════════════════════
// Todos los datos provienen de Supabase (radio_daily_metrics, etc.)
// o del backend PHP. Cero mocks en producción.
// ═══════════════════════════════════════════════════════════════

/** Períodos de análisis soportados (spec RFW-READDY-ANALYTICS-001) */
export type AnalyticsPeriod =
  | 'TODAY'
  | 'YESTERDAY'
  | 'LAST_7_DAYS'
  | 'LAST_30_DAYS'
  | 'THIS_MONTH'
  | 'LAST_MONTH'
  | 'CUSTOM';

export interface DateRange {
  start: string; // ISO 8601 date string (YYYY-MM-DD)
  end: string;   // ISO 8601 date string (YYYY-MM-DD)
}

/** Etiquetas en español para cada período */
export const ANALYTICS_PERIOD_LABELS: Record<AnalyticsPeriod, string> = {
  TODAY: 'Hoy',
  YESTERDAY: 'Ayer',
  LAST_7_DAYS: '7 días',
  LAST_30_DAYS: '30 días',
  THIS_MONTH: 'Este mes',
  LAST_MONTH: 'Mes pasado',
  CUSTOM: 'Personalizado',
};

/** Períodos que se muestran como botones rápidos (sin CUSTOM) */
export const ANALYTICS_PERIOD_PRESETS: AnalyticsPeriod[] = [
  'TODAY',
  'YESTERDAY',
  'LAST_7_DAYS',
  'LAST_30_DAYS',
  'THIS_MONTH',
  'LAST_MONTH',
];

// ═══════════════════════════════════════════════════════════════
// MÉTRICAS AGREGADAS (fuente: radio_daily_metrics)
// ═══════════════════════════════════════════════════════════════

export interface AggregatedMetrics {
  totalListeners: number;
  totalSessions: number;
  totalListeningSeconds: number;
  avgSessionSeconds: number;
  peakConcurrent: number;
}

// ═══════════════════════════════════════════════════════════════
// MÉTRICAS DE SEGUIDORES (fuente: radio_followers)
// ═══════════════════════════════════════════════════════════════

export interface FollowerMetrics {
  totalFollowers: number;
  newFollowers: number;
}

// ═══════════════════════════════════════════════════════════════
// MÉTRICAS DE CONTENIDO (fuente: programs, podcasts, podcast_episodes)
// ═══════════════════════════════════════════════════════════════

export interface ContentMetrics {
  totalPrograms: number;
  activePrograms: number; // is_active = true
  totalPodcasts: number;
  totalEpisodes: number;
}

// ═══════════════════════════════════════════════════════════════
// MÉTRICAS DE ENGAGEMENT (fuente: chat_messages via chat_rooms)
// ═══════════════════════════════════════════════════════════════

export interface EngagementMetrics {
  totalChatMessages: number;
  totalSongRequests: number;
  totalGreetings: number;
}

// ═══════════════════════════════════════════════════════════════
// MÉTRICAS DE ESTACIONES (fuente: stations)
// ═══════════════════════════════════════════════════════════════

export interface StationMetrics {
  totalStations: number;
  activeStations: number; // status = 'active'
  streamAvailability: number; // porcentaje 0-100
}

// ═══════════════════════════════════════════════════════════════
// PUNTO DIARIO (para gráficas)
// ═══════════════════════════════════════════════════════════════

export interface DailyMetricPoint {
  date: string; // YYYY-MM-DD
  listeners: number;
  sessions: number;
  listeningSeconds: number;
  peakConcurrent: number;
}

// ═══════════════════════════════════════════════════════════════
// TOP CONTENT
// ═══════════════════════════════════════════════════════════════

export interface TopContentItem {
  id: string;
  name: string;
  type: 'program' | 'podcast';
  value: number;
  unit: string;
  subtitle?: string;
}

// ═══════════════════════════════════════════════════════════════
// PAQUETE COMPLETO DE ANALYTICS
// ═══════════════════════════════════════════════════════════════

export interface RadioAnalytics {
  radioId: string;
  radioName: string;
  period: AnalyticsPeriod;
  dateRange: DateRange;
  metrics: AggregatedMetrics;
  followers: FollowerMetrics;
  content: ContentMetrics;
  engagement: EngagementMetrics;
  stations: StationMetrics;
  dailyBreakdown: DailyMetricPoint[];
  topPrograms: TopContentItem[];
  topPodcasts: TopContentItem[];
}

// ═══════════════════════════════════════════════════════════════
// RADIO CONTEXT (para el selector de radio en analytics)
// ═══════════════════════════════════════════════════════════════

export interface AnalyticsRadioContext {
  radioId: string;
  radioName: string;
  radioSlug: string;
  logoUrl?: string;
}

// ═══════════════════════════════════════════════════════════════
// HELPERS
// ═══════════════════════════════════════════════════════════════

/** Convierte un período + timezone opcional a un rango de fechas */
export function periodToDateRange(period: AnalyticsPeriod, customRange?: DateRange, timezone?: string): DateRange {
  const tz = timezone || 'UTC';
  const now = new Date();
  const today = new Date(now.toLocaleString('en-US', { timeZone: tz }));
  const todayStr = today.toISOString().slice(0, 10);

  const addDays = (d: Date, n: number): string => {
    const dd = new Date(d);
    dd.setDate(dd.getDate() + n);
    return dd.toISOString().slice(0, 10);
  };

  switch (period) {
    case 'TODAY':
      return { start: todayStr, end: todayStr };
    case 'YESTERDAY':
      return { start: addDays(today, -1), end: addDays(today, -1) };
    case 'LAST_7_DAYS':
      return { start: addDays(today, -6), end: todayStr };
    case 'LAST_30_DAYS':
      return { start: addDays(today, -29), end: todayStr };
    case 'THIS_MONTH': {
      const start = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-01`;
      return { start, end: todayStr };
    }
    case 'LAST_MONTH': {
      const prevMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      const lastDay = new Date(today.getFullYear(), today.getMonth(), 0);
      return {
        start: prevMonth.toISOString().slice(0, 10),
        end: lastDay.toISOString().slice(0, 10),
      };
    }
    case 'CUSTOM':
      if (!customRange) {
        return { start: addDays(today, -7), end: todayStr };
      }
      return customRange;
    default:
      return { start: addDays(today, -7), end: todayStr };
  }
}

/** Valida que un rango personalizado sea razonable (máx 365 días) */
export function validateCustomRange(range: DateRange): { valid: boolean; error?: string } {
  const start = new Date(range.start);
  const end = new Date(range.end);
  if (isNaN(start.getTime()) || isNaN(end.getTime())) {
    return { valid: false, error: 'Fechas inválidas' };
  }
  if (start > end) {
    return { valid: false, error: 'La fecha inicial no puede ser mayor que la final' };
  }
  const diffDays = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
  if (diffDays > 365) {
    return { valid: false, error: 'El rango máximo es de 365 días' };
  }
  return { valid: true };
}